import { ScheduleRowEntity } from '../apis/schedule/schedule-row-entity';
import { SerieSettingEntity } from '../apis/serie/serie-setting-entity';
import { TeamSettingEntity } from '../apis/team/team-setting-entity';
import { ScheduleViewModel } from '../components/schedule/schedule-view-model';

export class Penalty {

    public static calculate(scheduleRows: ScheduleRowEntity[], serie: SerieSettingEntity, teams: TeamSettingEntity[]): Penalty {

        // When teams come from multiple groups, same team names in different groups
        // would be mismatched by name-only lookup. Calculate per-group and aggregate.
        const groupIds = [...new Set(teams.map(t => t.groupId).filter(Boolean))];
        if (groupIds.length > 1) {
            // Rows always carry their own groupId, which is unambiguous even when
            // groups share team names. Split rows by groupId and aggregate.
            return Penalty.aggregate(groupIds.map(groupId => {
                const groupTeams = teams.filter(t => t.groupId === groupId);
                const groupRows = scheduleRows.filter(r => r.groupId === groupId);
                return Penalty.calculate(groupRows, serie, groupTeams);
            }));
        }

        const penalty = new Penalty();

        let id = 0;
        for (const team of teams) {
            team.id = id.toString();
            id++;
        }
        let games: PenaltyGameModel[] = [];
        for (const scheduleRow of scheduleRows) {
            const game = new PenaltyGameModel(scheduleRow, teams);
            games.add(game);
        }
        penalty.isNotValid = games.any(g => g.isInvalid);
        penalty.hasRemovedTeams = games.any(g => g.hasRemovedTeam);
        penalty.reminderTeam = teams.where(t => games.where(g => g.homeTeam === t || g.opponentTeam === t).length > t.getEffectiveGameCount(serie.gameCount)).firstOrDefault();
        games = games.where(g => g.homeTeam != null && g.opponentTeam != null);

        if (!games.any()) {
            return penalty;
        }

        const intervalCount = games.orderBy(g => g.interval).lastOrDefault().interval + 1;
        let teamsInInterval = this.getTeamsInInterval(games, 0);

        for (let interval = 1; interval < intervalCount; interval++) {
            const teamsInPreviousInterval = teamsInInterval;
            teamsInInterval = this.getTeamsInInterval(games, interval);
            const intersection = teamsInInterval.intersect(teamsInPreviousInterval);
            penalty.adjacentIntervalCount += intersection.length;
            penalty.isNotValid = penalty.isNotValid || this.hasDuplicates(teamsInInterval);
        }

        for (const team of teams) {
            // Same opponent twice or more
            const opponents = this.getOpponents(games, team);
            let counts = opponents.groupBy(o => o, v => v).where(k => k.values.length > 1).select(k => k.values.length - 1);
            for (const count of counts) {
                if (!penalty.sameOpponentCount.has(count)) {
                    penalty.sameOpponentCount.set(count, 0);
                }
                const c = penalty.sameOpponentCount.get(count);
                penalty.sameOpponentCount.set(count, c + 1);
            }

            // Same opponent association twice or more
            counts = opponents.groupBy(o => o.association, v => v).where(k => k.values.length > 1).select(k => k.values.length - 1);
            for (const count of counts) {
                if (!penalty.sameOpponentAssociationCount.has(count)) {
                    penalty.sameOpponentAssociationCount.set(count, 0);
                }
                const c = penalty.sameOpponentAssociationCount.get(count);
                penalty.sameOpponentAssociationCount.set(count, c + 1);
            }

            // Own association as opponent
            const associationCount = opponents.where(o => o.association === team.association).length;
            if (associationCount > 0) {
                if (!penalty.ownAssociationAsOpponentCount.has(associationCount)) {
                    penalty.ownAssociationAsOpponentCount.set(associationCount, 0);
                }
                const c = penalty.ownAssociationAsOpponentCount.get(associationCount);
                penalty.ownAssociationAsOpponentCount.set(associationCount, c + 1);
            }

            // Extra intervals from optimal
            const teamIntervals = this.getGamesForTeam(games, team).select(g => g.interval).orderBy(i => i);
            if (teamIntervals.any()) {
                const extraTeamIntervals = Math.max(0, teamIntervals.lastOrDefault() - teamIntervals.firstOrDefault() + 1 - (team.getEffectiveGameCount(serie.gameCount) * 2 - 1));
                penalty.notOptimizedTotalTimeCount += extraTeamIntervals;
            }

        }

        penalty.associationAgainstAssociationCount = this.getAGAScore(games, teams);
        penalty.associationAgainstAssociationCount = penalty.associationAgainstAssociationCount / 2;
        const optimalIntervalCount = this.calculateOptimalIntervalCount(teams, serie);
        penalty.extraIntervals = Math.max(0, intervalCount - optimalIntervalCount);
        return penalty;
    }

    public static aggregate(penalties: Penalty[]): Penalty {
        const result = new Penalty();
        result.isNotValid = penalties.some(p => p.isNotValid);
        result.hasRemovedTeams = penalties.some(p => p.hasRemovedTeams);
        result.reminderTeam = penalties.map(p => p.reminderTeam).find(t => t != null) ?? null;
        result.adjacentIntervalCount = penalties.reduce((s, p) => s + p.adjacentIntervalCount, 0);
        result.extraIntervals = 0; // Not meaningful for multi-group schedules — groups interleave intentionally
        result.notOptimizedTotalTimeCount = penalties.reduce((s, p) => s + p.notOptimizedTotalTimeCount, 0);
        result.associationAgainstAssociationCount = penalties.reduce((s, p) => s + p.associationAgainstAssociationCount, 0);
        for (const p of penalties) {
            p.sameOpponentCount.forEach((v, k) => {
                result.sameOpponentCount.set(k, (result.sameOpponentCount.get(k) ?? 0) + v);
            });
            p.sameOpponentAssociationCount.forEach((v, k) => {
                result.sameOpponentAssociationCount.set(k, (result.sameOpponentAssociationCount.get(k) ?? 0) + v);
            });
            p.ownAssociationAsOpponentCount.forEach((v, k) => {
                result.ownAssociationAsOpponentCount.set(k, (result.ownAssociationAsOpponentCount.get(k) ?? 0) + v);
            });
        }
        return result;
    }

    public static getAGAScore(games: PenaltyGameModel[], teams: TeamSettingEntity[]): number {
        let score = 0;
        let associations = teams.select(t => t.association);
        associations = associations.distinct();
        for (const association of associations) {
            const associationTeamCount = teams.where(t => t.association === association).length;
            // const homeTeamAssociation = teams.where(t => t.association === games.h).length;
            const allowLimitWithoutPenalty = Math.max(0, Math.ceil(associationTeamCount / 2.0) - 1); // Large associations must meat same associations in some degree
            const gamesForAssociation = games.where(g => g.homeTeam.association === association || g.opponentTeam.association === association);
            const opponentAssociations = gamesForAssociation.select(g => g.homeTeam.association === association ? g.opponentTeam.association : g.homeTeam.association);
            const grouped = opponentAssociations.groupBy(oa => oa, v => v);
            const mooreThanOne = grouped.where(g => g.values.length > 1);
            const count = Math.max(0, mooreThanOne.sum(g => g.values.length - 1) - allowLimitWithoutPenalty);
            score += this.getSameAssociationAgainstAssociationPenalty(count);
        }
        return Math.floor(score / 2);
    }

    public static calculateGameCount(teams: TeamSettingEntity[], serieGameCount: number): number {
        let gameCount = teams.sum(t => t.getEffectiveGameCount(serieGameCount));
        const reminder = gameCount % 2.0;
        const hasReminder = reminder > 0;
        gameCount = Math.floor(gameCount / 2);
        if (hasReminder) {
            gameCount++;
        }
        return gameCount;
    }

    public static calculateOptimalIntervalCount(teams: TeamSettingEntity[], serie: SerieSettingEntity): number {
        const gameCount = this.calculateGameCount(teams, serie.gameCount);
        const reminder = gameCount % serie.fields;
        let intervalCount = Math.floor(gameCount / serie.fields);
        if (reminder > 0) {
            intervalCount++;
        }
        return Math.ceil(intervalCount);
    }
    public static getSameAssociationAgainstAssociationPenalty(count: number): number {
        switch (count) {
            case 1:
                return 1;
            case 2:
                return 5;
            case 3:
                return 15;
            case 4:
                return 20;
            case 5:
                return 25;
            case 6:
                return 30;
        }
        return 0;
    }
    public static getGamesForTeam(games: PenaltyGameModel[], team: TeamSettingEntity): PenaltyGameModel[] {
        return games.where(g => g.homeTeam.id === team.id || g.opponentTeam.id === team.id);
    }
    public static getTeamsInInterval(games: PenaltyGameModel[], interval: number): TeamSettingEntity[] {
        return games.where(g => g.interval === interval).selectMany(g => [g.homeTeam, g.opponentTeam]);
    }

    public static getOpponents(games: PenaltyGameModel[], team: TeamSettingEntity): TeamSettingEntity[] {
        const opponents = games.where(g => g.homeTeam.id === team.id).select(g => g.opponentTeam);
        const opponents2 = games.where(g => g.opponentTeam.id === team.id).select(g => g.homeTeam);
        opponents.addRange(opponents2);
        return opponents;
    }

    // private static hasDuplicates(teams: TeamSettingEntity[]): boolean {
    //     return teams.length !== new Set(teams).size;
    // }

    private static hasDuplicates(array: TeamSettingEntity[]): boolean {
        const valuesSoFar: TeamSettingEntity[] = [];
        for (const value of array) {
            if (valuesSoFar.indexOf(value) !== -1) {
                return true;
            }
            valuesSoFar.push(value);
        }
        return false;
    }

    public sameOpponentCount: Map<number, number>;
    public sameOpponentAssociationCount: Map<number, number>;
    public ownAssociationAsOpponentCount: Map<number, number>;
    public adjacentIntervalCount: number = 0;
    public extraIntervals: number = 0;
    public notOptimizedTotalTimeCount: number = 0;
    public associationAgainstAssociationCount: number = 0;
    public isNotValid: boolean = false;

    public hasRemovedTeams: boolean = false;
    public reminderTeam: TeamSettingEntity = null;
    public get totalSameOpponentCount(): number {
        let total = 0;
        this.sameOpponentCount.forEach((v, k, _m) => { total += (v * k); });
        return total;
    }
    public get totalSameOpponentAssociationCount(): number {
        let total = 0;
        this.sameOpponentAssociationCount.forEach((v, k, _m) => { total += (v * k); });
        return total;
    }
    public get totalOwnAssociationAsOpponentCount(): number {
        let total = 0;
        this.ownAssociationAsOpponentCount.forEach((v, k, _m) => { total += (v * k); });
        return total / 2;
    }

    public get noRemarks(): boolean {
        return !this.isNotValid && !this.hasRemovedTeams && !this.hasReminderTeam && this.extraIntervals === 0 && this.adjacentIntervalCount === 0 && this.totalSameOpponentCount === 0 && this.totalSameOpponentAssociationCount === 0 && this.totalOwnAssociationAsOpponentCount === 0;
    }

    public get hasReminderTeam(): boolean {
        return this.reminderTeam != null;
    }

    constructor() {
        this.sameOpponentCount = new Map<number, number>();
        this.sameOpponentAssociationCount = new Map<number, number>();
        this.ownAssociationAsOpponentCount = new Map<number, number>();
    }
    public toString(): string {
        return `SameOpponentCount: ${this.totalSameOpponentCount}, SameOpponentAssociationCount: ${this.totalSameOpponentAssociationCount}, OwnAssociationAsOpponentCount: ${this.totalOwnAssociationAsOpponentCount}, adjacentIntervalCount: ${this.adjacentIntervalCount}, extraIntervals: ${this.extraIntervals}, notOptimizedTotalTimeCount: ${this.notOptimizedTotalTimeCount}, associationAgainstAssociationCount: ${this.associationAgainstAssociationCount} `;
        // return `SameOpponentCount: ${this.totalSameOpponentCount}, SameOpponentAssociationCount: ${this.totalSameOpponentAssociationCount}, ownAssociationAsOpponentCount:  ${this.totalOwnAssociationAsOpponentCount} adjacentIntervalCount: ${this.adjacentIntervalCount}, extraIntervals: ${this.extraIntervals}, notOptimizedTotalTimeCount: ${this.notOptimizedTotalTimeCount}, associationAgainstAssociationCount: ${this.associationAgainstAssociationCount} `;
    }

}

export class PenaltyGameModel {
    public homeTeam: TeamSettingEntity = null;
    public opponentTeam: TeamSettingEntity = null;
    public fieldId: number = null;
    public interval: number = null;
    private _scheduleRow: ScheduleRowEntity = null;
    public get isInvalid(): boolean {
        return (this._scheduleRow.opponentTeam !== ScheduleViewModel.freeSlotTeamName && this._scheduleRow.homeTeam === ScheduleViewModel.freeSlotTeamName)
        || (this._scheduleRow.homeTeam !== ScheduleViewModel.freeSlotTeamName && this._scheduleRow.opponentTeam === ScheduleViewModel.freeSlotTeamName);
    }

    public get hasRemovedTeam(): boolean {
        return (this.opponentTeam == null && this._scheduleRow.opponentTeam !== ScheduleViewModel.freeSlotTeamName) || (this.homeTeam == null && this._scheduleRow.homeTeam !== ScheduleViewModel.freeSlotTeamName);
    }

    public get id(): string {
        return this._scheduleRow.id;
    }

    constructor(scheduleRow: ScheduleRowEntity, teams: TeamSettingEntity[]) {
        this._scheduleRow = scheduleRow;
        this.fieldId = scheduleRow.fieldId;
        this.interval = scheduleRow.interval;
        this.homeTeam = teams.firstOrDefault(t => scheduleRow.homeTeam === t.name);
        this.opponentTeam = teams.firstOrDefault(t => scheduleRow.opponentTeam === t.name);
    }
}
